import { useMemo, useState } from 'react';
import { Search, ImageOff, BookX } from 'lucide-react';
import type { Exercise, ExerciseCategory } from '../../types/exercise';
import { CATEGORY_LABEL, CATEGORY_ORDER } from '../../types/exercise';
import { EXERCISES, requireExerciseById } from '../../data/exercises';
import { PageHeader } from '../layout/PageHeader';
import { LwsBadge } from '../training/LwsBadge';
import { ExerciseDetail } from '../training/ExerciseDetail';
import { ExerciseIllustration } from '../training/ExerciseIllustration';
import { EmptyState } from '../ui/EmptyState';

type Filter = 'all' | ExerciseCategory;

export function LibraryView({ dayKey }: { dayKey: string }): JSX.Element {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const sorted = useMemo(
    () => [...EXERCISES].sort((a, b) => a.name.localeCompare(b.name, 'de')),
    [],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((ex) => {
      if (filter !== 'all' && !ex.categories.includes(filter)) return false;
      if (!q) return true;
      return (
        ex.name.toLowerCase().includes(q) ||
        ex.muscles.toLowerCase().includes(q)
      );
    });
  }, [sorted, query, filter]);

  const openExerciseObj = openId ? requireExerciseById(openId) : null;

  return (
    <main className="min-h-dvh bg-zinc-50 text-zinc-900 font-sans">
      <PageHeader title="Bibliothek" eyebrow={`${EXERCISES.length} Uebungen`} />

      <div className="mx-auto max-w-3xl px-4 pt-4 pb-24 md:px-6 md:pb-8">
        <label className="relative block">
          <span className="sr-only">Uebung suchen</span>
          <Search
            aria-hidden="true"
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-500"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Uebung suchen…"
            className="h-11 w-full rounded-lg border border-zinc-200 bg-white pl-9 pr-3 text-base text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
          />
        </label>

        <div className="mt-3 -mx-4 overflow-x-auto pb-1">
          <div className="flex w-max gap-2 px-4">
            <FilterChip
              label="Alle"
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            {CATEGORY_ORDER.map((cat) => (
              <FilterChip
                key={cat}
                label={CATEGORY_LABEL[cat]}
                active={filter === cat}
                onClick={() => setFilter(cat)}
              />
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            Icon={BookX}
            heading="Keine Uebung gefunden"
            body="Versuche einen anderen Suchbegriff oder entferne Filter."
          />
        ) : (
          <ul className="mt-4 flex flex-col gap-2">
            {filtered.map((ex) => (
              <li key={ex.id}>
                <LibraryCard exercise={ex} onOpen={setOpenId} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {openExerciseObj ? (
        <ExerciseDetail
          exercise={openExerciseObj}
          dayKey={dayKey}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </main>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, active, onClick }: FilterChipProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex h-11 items-center rounded-full border px-4 text-sm font-semibold transition-transform duration-[120ms] active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2 ${
        active
          ? 'border-emerald-500 bg-emerald-500 text-white'
          : 'border-zinc-200 bg-white text-zinc-700'
      }`}
    >
      {label}
    </button>
  );
}

function LibraryCard({
  exercise,
  onOpen,
}: {
  exercise: Exercise;
  onOpen: (id: string) => void;
}): JSX.Element {
  const lwsVariant = exercise.lwsNote ? 'caution' : 'default';
  return (
    <button
      type="button"
      onClick={() => onOpen(exercise.id)}
      aria-label={`${exercise.name} oeffnen`}
      className="flex w-full items-start gap-3 rounded-xl border border-zinc-200 bg-white p-4 text-left transition-transform duration-[120ms] active:scale-[0.97] active:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
    >
      <span
        aria-hidden="true"
        className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100 text-zinc-400"
      >
        {exercise.imagePath ? (
          <img
            src={exercise.imagePath}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        ) : exercise.iconKey ? (
          <ExerciseIllustration iconKey={exercise.iconKey} size="thumb" />
        ) : (
          <ImageOff className="size-6" />
        )}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-base font-semibold leading-tight text-zinc-900">
          {exercise.name}
        </span>
        <span className="truncate text-sm text-zinc-600">{exercise.muscles}</span>
        <span className="flex flex-wrap gap-1">
          {exercise.categories.map((c) => (
            <span
              key={c}
              className="inline-flex h-5 items-center rounded-full border border-zinc-200 bg-zinc-50 px-2 text-[11px] font-semibold text-zinc-600"
            >
              {CATEGORY_LABEL[c]}
            </span>
          ))}
        </span>
      </span>

      <span className="shrink-0">
        <LwsBadge variant={lwsVariant} note={exercise.lwsNote} />
      </span>
    </button>
  );
}
